import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Coins, Ruler, FileText, Scale, FileCheck2, Handshake, RefreshCw, Umbrella } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { SERVICES, waLink, SERVICE_ICONS, IMG_DIMS, srcSetFor } from "@/lib/content";

const ICONS = { coins: Coins, ruler: Ruler, file: FileText, scale: Scale, filecheck: FileCheck2, handshake: Handshake, refresh: RefreshCw, umbrella: Umbrella };

const WAIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35zM12.05 21.5h-.01a9.4 9.4 0 0 1-4.8-1.32l-.34-.2-3.57.94.95-3.48-.22-.36a9.43 9.43 0 1 1 7.99 4.42zm8.02-17.45A11.27 11.27 0 0 0 12.05.75C5.8.75.72 5.83.72 12.08c0 2 .52 3.95 1.52 5.66L.62 23.62l6.02-1.58a11.3 11.3 0 0 0 5.41 1.38h.01c6.25 0 11.33-5.08 11.33-11.33 0-3.03-1.18-5.87-3.32-8.01z" />
  </svg>
);

const tone = (s) =>
  s.dark
    ? "bg-[#233D25] text-[#F9F8F3] border-[#233D25]"
    : s.ochre
    ? "bg-[#5D995E] text-[#0F1F10] border-[#5D995E]"
    : s.img
    ? "bg-[#1B2F1D] text-[#F9F8F3] border-[#1B2F1D]"
    : "bg-white text-[#1A2218] border-[#E5E0D5]";

// Grade uniforme: 8 cards — 4 colunas no desktop, 2 no tablet, 1 no mobile.
const Card = ({ s, i }) => {
  const muted = s.dark || s.img ? "text-[#F9F8F3]/80" : s.ochre ? "text-[#0F1F10]/80" : "text-[#4A5549]";
  const Icon = ICONS[SERVICE_ICONS[s.id]] || FileText;
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay: (i % 4) * 0.06, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      className={`group relative flex h-full flex-col overflow-hidden rounded-[24px] border ${tone(s)}`}
    >
      <Link
        to={`/${s.id}`}
        data-testid={`service-card-${s.id}`}
        title={`Ver detalhes de ${s.title}`}
        className="relative z-10 flex flex-1 flex-col"
      >
        {/* Mobile: linha compacta horizontal */}
        <div className="flex items-center gap-3 p-4 sm:hidden">
          <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${s.dark || s.img ? "bg-[#8FC690]/15 text-[#8FC690]" : s.ochre ? "bg-white/25 text-[#0F1F10]" : "bg-[#F2EFE9] text-[#233D25]"}`}>
            <Icon className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-serif text-lg leading-tight">{s.title}</span>
            <span className={`block truncate font-mono text-[11px] uppercase tracking-[0.12em] ${muted}`}>{s.tag}</span>
          </span>
          <ArrowUpRight className="h-4 w-4 shrink-0 text-[#5D995E]" />
        </div>
        {/* sm+: imagem (altura máx. 220px no desktop) + conteúdo */}
        <div className="relative hidden sm:block">
          {s.img ? (
            <div className="relative h-[220px] max-h-[220px] overflow-hidden">
              <img src={s.img} alt={s.alt || s.title} srcSet={srcSetFor(s.img)} sizes="(min-width:1024px) 25vw, (min-width:640px) 50vw, calc(100vw - 40px)" width={(IMG_DIMS[s.img] || [1400, 1050])[0]} height={(IMG_DIMS[s.img] || [1400, 1050])[1]} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1B2F1D]/85 via-[#1B2F1D]/30 to-transparent" />
            </div>
          ) : (
            <div className="flex items-center justify-between p-6 pb-0">
              <span className={`grid h-10 w-10 place-items-center rounded-full ${s.dark ? "bg-[#8FC690]/15 text-[#8FC690]" : s.ochre ? "bg-white/25 text-[#0F1F10]" : "bg-[#F2EFE9] text-[#233D25]"}`}>
                <Icon className="h-5 w-5" />
              </span>
            </div>
          )}
        </div>
        <div className="hidden flex-1 flex-col p-6 pt-4 sm:flex">
          <p className={`font-mono text-xs uppercase tracking-[0.15em] ${muted}`}>{s.tag}</p>
          <h3 className={`mt-2 font-serif text-2xl leading-[1.1] tracking-tight ${s.img ? "sm:text-3xl" : ""}`}>{s.title}</h3>
          <p className={`mt-2 text-sm leading-relaxed ${muted}`}>{s.text}</p>
          <span className="mt-auto inline-flex items-center gap-2 pt-4 text-sm font-semibold">
            Ver detalhes
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

export const Services = () => (
  <section id="servicos" data-testid="services-section" className="bg-[#F2EFE9] py-10 sm:py-16 md:py-36">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="mb-10 flex flex-col justify-between gap-6 sm:mb-14 md:mb-20 lg:flex-row lg:items-end">
        <div>
          <Reveal><Overline>Serviços</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-3xl font-serif text-4xl leading-[1.05] tracking-tight text-[#1A2218] sm:mt-8 sm:text-5xl lg:text-6xl">
              Tudo o que a propriedade precisa, <em className="text-[#5D995E]">em um só lugar.</em>
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <p className="max-w-sm text-base leading-relaxed text-[#4A5549]">
            Oito frentes técnicas que conversam entre si — do projeto de crédito ao laudo pericial. Toque em um serviço para ver os detalhes.
          </p>
        </Reveal>
      </div>

      <div data-testid="services-grid" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map((s, i) => (
          <Card key={s.id} s={s} i={i} />
        ))}
      </div>

      <Reveal delay={0.1}>
        <div data-testid="services-cta-card" className="mt-4 flex flex-col justify-between gap-4 rounded-[24px] bg-[#1B2F1D] p-5 text-[#F9F8F3] sm:mt-5 sm:flex-row sm:items-center sm:p-6">
          <p className="max-w-md text-base leading-relaxed text-[#F9F8F3]/85">
            <span className="font-serif text-xl text-[#F9F8F3] sm:text-2xl">Não encontrou o seu caso?</span>{" "}
            Cada propriedade tem uma realidade — conte o que está acontecendo e montamos o caminho técnico certo.
          </p>
          <a
            data-testid="services-cta-whatsapp"
            href={waLink("Olá! Tenho uma demanda que não está na lista de serviços.")}
            target="_blank"
            rel="noreferrer"
            className="inline-flex w-fit shrink-0 items-center gap-3 rounded-full bg-[#F9F8F3] px-6 py-3.5 text-sm font-semibold text-[#16220F] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#8FC690]"
          >
            <WAIcon className="h-4 w-4" /> Falar com a equipe
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);
